import type { HTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from '../internal/cx';
import styles from './Card.module.css';

export type HeadingTag = 'h2' | 'h3' | 'h4';

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Rendered in a CardHeader. Omit to compose CardHeader/CardBody/CardFooter yourself. */
  title?: ReactNode;
  description?: ReactNode;
  /** Header-right actions (buttons, menus). */
  actions?: ReactNode;
  footer?: ReactNode;
  titleAs?: HeadingTag;
  /** Semantic element. Use `section` with a title for landmark-worthy groups. */
  as?: 'div' | 'section' | 'article';
  /** Remove body padding (e.g. for edge-to-edge tables). */
  flush?: boolean;
  ref?: Ref<HTMLElement>;
}

export function Card({
  title,
  description,
  actions,
  footer,
  titleAs = 'h2',
  as: Element = 'div',
  flush = false,
  className,
  children,
  ref,
  ...rest
}: CardProps) {
  const hasHeader = title != null || actions != null;
  return (
    <Element ref={ref as Ref<HTMLDivElement>} className={cx(styles.card, className)} {...rest}>
      {hasHeader && (
        <CardHeader title={title} description={description} actions={actions} titleAs={titleAs} />
      )}
      {hasHeader || footer != null ? (
        <CardBody flush={flush}>{children}</CardBody>
      ) : (
        <div className={cx(!flush && styles.body)}>{children}</div>
      )}
      {footer != null && <CardFooter>{footer}</CardFooter>}
    </Element>
  );
}

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  titleAs?: HeadingTag;
}

export function CardHeader({
  title,
  description,
  actions,
  titleAs: Heading = 'h2',
  className,
  children,
  ...rest
}: CardHeaderProps) {
  return (
    <div className={cx(styles.header, className)} {...rest}>
      <div className={styles.headingGroup}>
        {title != null && <Heading className={styles.title}>{title}</Heading>}
        {description != null && <p className={styles.description}>{description}</p>}
        {children}
      </div>
      {actions != null && <div className={styles.actions}>{actions}</div>}
    </div>
  );
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  flush?: boolean;
}

export function CardBody({ flush = false, className, ...rest }: CardBodyProps) {
  return <div className={cx(styles.body, flush && styles.flush, className)} {...rest} />;
}

export function CardFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx(styles.footer, className)} {...rest} />;
}
